import { requiredTranslatableZodSchema } from "@@/shared/translations/translation";
import { z } from "zod";
import { agendaPreset_dynamicTimeZodEnum } from "./agenda-preset.dynamic-time";

/**
 * Zod schema object for the dynamic time preset in the agenda.
 * The preset is used to filter the agenda based on dynamic time ranges.
 *
 * This schema validates the following properties:
 * - `label`: A required translatable schema.
 * - `value`: An enum value representing the dynamic time preset.
 * - `visible`: An optional boolean indicating whether the preset is visible. Defaults to `true`.
 */
const agendaDynamicTimePresetZodObject = z.object({
    _id: z.string(),
    _type: z.literal("AGENDA_DYNAMIC_TIME_PRESET"),
    name: requiredTranslatableZodSchema,
    value: agendaPreset_dynamicTimeZodEnum,
    visible: z.boolean().default(true).optional(),
});

/**
 * Represents a dynamic time preset in the agenda.
 * The preset is used to filter the agenda based on dynamic times like
 * today, tomorrow, this week, etc. The preset can be visible or hidden.
 * When hidden it's not displayed in the UI as a preset.
 */
type AgendaDynamicTimePreset = z.infer<typeof agendaDynamicTimePresetZodObject>;

const agendaDynamicTimePresetFactory = (params: AgendaDynamicTimePreset) =>
    agendaDynamicTimePresetZodObject.parse(params);

export { agendaDynamicTimePresetFactory, agendaDynamicTimePresetZodObject };
export type { AgendaDynamicTimePreset };
